import {
  Component, inject, input, output,
  signal, effect,
  viewChild, ElementRef,
  DestroyRef, afterNextRender,
  ApplicationRef, EnvironmentInjector, createComponent,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import OlMap from 'ol/Map';
import Overlay from 'ol/Overlay';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import { fromLonLat, toLonLat, transform } from 'ol/proj';
import { Style, Icon } from 'ol/style';
import { click } from 'ol/events/condition';
import Select from 'ol/interaction/Select';
import { MapBrowserEvent } from 'ol';

import proj4 from 'proj4';
import { register } from 'ol/proj/proj4';
import { MapService }  from '@libs/front/services/map/map.service';
import { DeviceService } from 'src/app/services/device.service';
import { MapViewMode } from '@libs/front/services/map/map.types';
import { OrthophotoMapService } from 'src/app/services/map.services';
import { SiteFeature, MarkerState, FlyTarget } from './sites-map.types';
import { CartePopupComponent } from '@libs/front/components/popup-panel/popup-panel.component';
import { ButtonComponent } from '@libs/front/components/button/button.component';
import MapConstants from 'src/app/components/carte/constants';
import { SitesService } from 'src/app/services/sites.service';

// ── Constantes visuelles ───────────────────────────────────────────────────
const COLOR_TEAL    = '#00C79A';
const COLOR_WHITE   = '#ffffff';
const COLOR_DEFAULT = '#6C1D45';
const RADIUS        = 18;
const ICON_SIZE     = 18;
const CANVAS_SIZE   = RADIUS * 2 + 6;
const GLOBE_SRC     = 'assets/icon/new_icon/globe.svg';

// Projection de la vue OL (source de evt.coordinate — ne change jamais)
const MAP_PROJECTION        = MapConstants.EPSG_REFERENCE_OL;  // EPSG:3857
// Projection d'affichage par défaut pour les sites sans EPSG (aligné avec carte.component)
const DEFAULT_DISPLAY_PROJ  = MapConstants.EPSG_WGS84;         // EPSG:4326
const DEFAULT_DISPLAY_LABEL = `EPSG:${DEFAULT_DISPLAY_PROJ.split(':')[1]}`; // "EPSG:4326"

// Coordonnées WGS84 valides : lon [-180,180] lat [-90,90]
// Un site sans EPSG peut avoir des coordonnées locales hors plage → on l'exclut de la carte
function isValidLonLat(lon: number, lat: number): boolean {
  return isFinite(lon) && isFinite(lat)
    && lon >= -180 && lon <= 180
    && lat >= -90  && lat <= 90;
}

@Component({
  selector:    'app-sites-map',
  templateUrl: './sites-map.component.html',
  styleUrls:   ['./sites-map.component.scss'],
  standalone:  true,
  imports:     [CommonModule, CartePopupComponent, ButtonComponent],
})
export class SitesMapComponent {

  protected device = inject(DeviceService);

  // ── Inputs ────────────────────────────────────────────────────────────────
  sites                 = input<SiteFeature[]>([]);
  selectedRegionSiteIds = input<(string | number)[]>([]);
  selectedSiteId        = input<string | number | null>(null);
  flyTo                 = input<FlyTarget | null>(null);
  closePopupTrigger     = input(0);

  // ── Outputs ───────────────────────────────────────────────────────────────
  siteClicked    = output<SiteFeature>();
  markerSelected = output<SiteFeature>();
  popupClosed    = output<void>();          // ← NOUVEAU : notifie area quand la popup se ferme

  // ── Refs DOM ──────────────────────────────────────────────────────────────
  private mapContainer = viewChild.required<ElementRef<HTMLDivElement>>('mapContainer');
  private scaleTextEl  = viewChild.required<ElementRef<HTMLSpanElement>>('scaleText');
  private scaleBarEl   = viewChild.required<ElementRef<HTMLDivElement>>('scaleBar');

  // ── Services ──────────────────────────────────────────────────────────────
  private mapService    = inject(MapService);
  private orthoService  = inject(OrthophotoMapService);
  private destroyRef    = inject(DestroyRef);
  private appRef        = inject(ApplicationRef);
  private envInjector   = inject(EnvironmentInjector);
  private sitesService  = inject(SitesService);

  // ── State carte ───────────────────────────────────────────────────────────
  viewMode = signal<MapViewMode>('plan');
  protected mapReady = signal(false);

  protected map!: OlMap;
  private selectInteraction!: Select;
  private vectorSource = new VectorSource<Feature<Point>>();
  private vectorLayer!: VectorLayer<VectorSource>;
  private styleCache   = new Map<MarkerState, Style>();

  // ── Popup carte ───────────────────────────────────────────────────────────
  popupSite            = signal<SiteFeature | null>(null);
  popupPosition        = signal<[number, number] | null>(null);
  popupOpen            = signal(false);
  private localSelectedSiteId    = signal<string | number | null>(null);
  private lastOpenedExternalId: string | number | null = null;

  closePopup(): void {
    this.popupOpen.set(false);
    this.popupSite.set(null);
    this.popupPosition.set(null);
    this.localSelectedSiteId.set(null);   // ← reset : le marker repasse en region-selected
    this.lastOpenedExternalId = null; 
    this.popupClosed.emit();              // ← notifie area.component
  }

  private openPopup(site: SiteFeature): void {
    this.localSelectedSiteId.set(site.id);
    this.popupSite.set(site);
    this.popupPosition.set([site.lon, site.lat]);
    this.popupOpen.set(true);
    this.markerSelected.emit(site);
  }

  // ── State coordonnées (utilisé dans le template) ──────────────────────────
  // coordLabel et coordProjection sont dynamiques : mis à jour selon l'EPSG du site sélectionné
  coordLabel      = signal(DEFAULT_DISPLAY_LABEL);
  coordProjection = signal<string>(DEFAULT_DISPLAY_PROJ);
  coordX          = signal('--');
  coordY          = signal('--');

  // ── Globe pré-calculé ─────────────────────────────────────────────────────
  private globeImage:      HTMLImageElement | null = null;
  private globeImageWhite: HTMLImageElement | null = null;
  private globeReady = false;

  constructor() {
    this.preloadGlobe();

    afterNextRender(() => {
      this.initMap();
      this.mapReady.set(true);

      this.destroyRef.onDestroy(() => {
        if (this.selectInteraction) this.map.removeInteraction(this.selectInteraction);
        this.mapService.destroyMap(this.map);
        this.styleCache.clear();
      });
    });

    effect(() => {
      if (!this.mapReady()) return;
      const effectiveSelectedId = this.localSelectedSiteId() ?? this.selectedSiteId();
      const externalId = this.selectedSiteId();
      if (externalId && String(externalId) !== String(this.lastOpenedExternalId ?? '')) {
        const site = this.sites().find(s => String(s.id) === String(externalId));
        if (site) {
          this.lastOpenedExternalId = externalId;
          this.openPopup(site);
        }
      }
      this.updateMarkerStyles(this.sites(), this.selectedRegionSiteIds(), effectiveSelectedId);
    });

    effect(() => {
      const target = this.flyTo();
      if (!target || !this.mapReady()) return;
      if (!isValidLonLat(target.lon, target.lat)) return;
      this.map.getView().animate({
        center:   fromLonLat([target.lon, target.lat]),
        zoom:     target.zoom,
        duration: 800,
      });
    });

    effect(() => {
      if (this.closePopupTrigger() > 0) {
        this.localSelectedSiteId.set(null);
        this.lastOpenedExternalId = null;
        this.closePopup();
      }
    });

    // ── Projection EPSG dynamique selon le site sélectionné ────────────────
    effect(() => {
      const siteId = this.selectedSiteId() ?? this.localSelectedSiteId();
      if (!siteId) {
        this.coordProjection.set(DEFAULT_DISPLAY_PROJ);
        this.coordLabel.set(DEFAULT_DISPLAY_LABEL);
        return;
      }
      const site = this.sites().find(s => String(s.id) === String(siteId));
      const epsgStr  = site?.epsg;
      const epsgCode = Number(epsgStr);
      if (!epsgStr || isNaN(epsgCode) || epsgCode === 0) {
        this.coordProjection.set(DEFAULT_DISPLAY_PROJ);
        this.coordLabel.set(DEFAULT_DISPLAY_LABEL);
        return;
      }
      const epsgKey = `EPSG:${epsgCode}`;
      this.sitesService.getEpsgProjString(epsgCode).then(projString => {
        if (!projString) {
          this.coordProjection.set(DEFAULT_DISPLAY_PROJ);
          this.coordLabel.set(DEFAULT_DISPLAY_LABEL);
          return;
        }
        proj4.defs(epsgKey, projString);
        register(proj4);
        this.coordProjection.set(epsgKey);
        this.coordLabel.set(epsgKey);
      }).catch(() => {
        this.coordProjection.set(DEFAULT_DISPLAY_PROJ);
        this.coordLabel.set(DEFAULT_DISPLAY_LABEL);
      });
    });
  }

  // ── Init carte ────────────────────────────────────────────────────────────
  private initMap(): void {
    this.vectorLayer = new VectorLayer({
      source:     this.vectorSource,
      properties: { id: 'sites' },
    });

    this.map = this.mapService.createMap(this.mapContainer().nativeElement, {
      center:           MapConstants.DEFAULT_CENTER as [number, number],
      zoom:             MapConstants.DEFAULT_ZOOM_LEVEL,
      projectionFrom:   MapConstants.EPSG_WGS84,
      projectionTo:     MapConstants.EPSG_REFERENCE_OL,
      viewMode:         this.viewMode(),
      showMousePosition: false, // géré manuellement ci-dessous
    });

    this.mapService.addLayer(this.map, this.vectorLayer);

    this.setupPointerMove();
    this.setupCustomScale();
    this.setupClickInteraction();
    this.orthoService.getMapBoxPublicKey().then(token => {
      if (token) {
        const mapboxUrl = `https://api.mapbox.com/styles/v1/mapbox/satellite-v9/tiles/256/{z}/{x}/{y}@2x?access_token=${token}`;
        const base = this.map.getLayers().getArray().find(l => l.get('id') === 'base');
        if (base) this.map.removeLayer(base);
        this.map.getLayers().insertAt(0, this.mapService.buildBaseLayer(this.viewMode(), mapboxUrl));
        this.mapService.setSatelliteUrl(this.map, mapboxUrl); // conservé pour Maps/Plan toggle
      }
    }).catch(() => { /* conserve ArcGIS en fallback */ });

    this.setupPointerMove();   // coordonnées EPSG/X/Y
    this.setupCustomScale();   // scale bar
    this.setupClickInteraction();
  }

  // ── Coordonnées : pointermove ─────────────────────────────────────────────
  // On écoute l'événement OL pointermove et on met à jour les 3 propriétés
  // utilisées dans le template. Pas de contrôle OL MousePosition injecté dans
  // le DOM — on évite ainsi tous les problèmes de positionnement OL natif.
  private setupPointerMove(): void {
    this.map.on('pointermove', (evt: MapBrowserEvent<PointerEvent>) => {
      const proj = this.coordProjection();
      let displayCoords: number[];
      try {
        displayCoords = transform(evt.coordinate, MAP_PROJECTION, proj);
      } catch {
        displayCoords = evt.coordinate;
      }
      this.coordX.set(Math.round(displayCoords[0]).toLocaleString('fr-FR'));
      this.coordY.set(Math.round(displayCoords[1]).toLocaleString('fr-FR'));

      const hit = this.map.hasFeatureAtPixel(evt.pixel);
      this.mapContainer().nativeElement.style.cursor = hit ? 'pointer' : '';
    });

    // Quand la souris quitte la carte, on remet les tirets
    this.mapContainer().nativeElement.addEventListener('mouseleave', () => {
      this.coordX.set('--');
      this.coordY.set('--');
    });
  }

  // ── Zoom (méthodes appelées depuis le template) ───────────────────────────
  zoomIn(): void {
    const view = this.map.getView();
    view.animate({ zoom: (view.getZoom() ?? 0) + 1, duration: 250 });
  }

  zoomOut(): void {
    const view = this.map.getView();
    view.animate({ zoom: (view.getZoom() ?? 0) - 1, duration: 250 });
  }

  // ── Scale bar custom ──────────────────────────────────────────────────────
  private setupCustomScale(): void {
    this.map.getView().on('change:resolution', () => this.updateCustomScale());
    this.updateCustomScale();
  }

  private updateCustomScale(): void {
    const view        = this.map.getView();
    const resolution  = view.getResolution();
    if (!resolution) return;

    const dpi           = 25.4 / 0.28;
    const metersPerUnit = view.getProjection().getMetersPerUnit();
    const nominalCount  = resolution * dpi * metersPerUnit;
    const niceScale     = this.getNiceScale(nominalCount);
    // On contraint la largeur à 120 px max pour éviter le débordement (cf. capture 1)
    const rawWidth      = niceScale / metersPerUnit / resolution;
    const scaleWidth    = Math.min(rawWidth, 120);

    this.scaleTextEl().nativeElement.innerText  = this.formatScaleLabel(niceScale);
    this.scaleBarEl().nativeElement.style.width = `${scaleWidth}px`;
  }

  private getNiceScale(rawLength: number): number {
    const scales   = [1, 2, 5];
    const exponent = Math.floor(Math.log10(rawLength));
    const base     = Math.pow(10, exponent);
    const factor   = rawLength / base;
    let nice       = scales.find(s => s >= factor);
    if (!nice) nice = 10;
    return nice * base;
  }

  private formatScaleLabel(length: number): string {
    return length >= 1000 ? `${length / 1000} km` : `${length} m`;
  }

  // ── Globe préchargement ───────────────────────────────────────────────────
  private preloadGlobe(): void {
    const img = new Image();

    const onLoaded = () => {
      this.globeImage      = img;
      this.globeImageWhite = this.buildWhiteVersion(img);
      this.globeReady      = true;
      this.styleCache.clear();
      if (this.mapReady()) {
        this.updateMarkerStyles(this.sites(), this.selectedRegionSiteIds(), this.selectedSiteId());
      }
    };

    img.onload  = onLoaded;
    img.onerror = () => console.warn('[SitesMap] Globe icon failed to load:', GLOBE_SRC);
    img.src     = GLOBE_SRC;
    if (img.complete) onLoaded();
  }

  private buildWhiteVersion(source: HTMLImageElement): HTMLImageElement {
    const tmp  = document.createElement('canvas');
    tmp.width  = ICON_SIZE;
    tmp.height = ICON_SIZE;
    const ctx  = tmp.getContext('2d')!;
    ctx.drawImage(source, 0, 0, ICON_SIZE, ICON_SIZE);
    ctx.globalCompositeOperation = 'source-in';
    ctx.fillStyle = COLOR_WHITE;
    ctx.fillRect(0, 0, ICON_SIZE, ICON_SIZE);
    const result = new Image();
    result.src   = tmp.toDataURL();
    return result;
  }

  // ── Markers ───────────────────────────────────────────────────────────────
  private updateMarkerStyles(
    sites: SiteFeature[],
    regionSiteIds: (string | number)[],
    selectedSiteId: string | number | null,
  ): void {
    const regionSet   = new Set(regionSiteIds.map(id => String(id)));
    const incomingIds = new Set(sites.map(s => String(s.id)));

    const featureMap = new Map<string, Feature<Point>>(
      this.vectorSource.getFeatures().map(f => [String(f.getId()), f])
    );

    for (const [id, feature] of featureMap) {
      if (!incomingIds.has(id)) {
        this.vectorSource.removeFeature(feature);
        featureMap.delete(id);
      }
    }

    for (const site of sites) {
      const siteIdStr = String(site.id);

      // Exclut les sites avec coordonnées hors plage WGS84 (coordonnées locales sans EPSG)
      if (!isValidLonLat(site.lon, site.lat)) {
        const existing = featureMap.get(siteIdStr);
        if (existing) this.vectorSource.removeFeature(existing);
        continue;
      }

      const state: MarkerState = siteIdStr === String(selectedSiteId)
        ? 'site-selected'
        : regionSet.has(siteIdStr) ? 'region-selected' : 'default';

      let feature = featureMap.get(siteIdStr);

      if (!feature) {
        feature = new Feature<Point>({ geometry: new Point(fromLonLat([site.lon, site.lat])) });
        feature.setId(site.id);
        feature.set('siteData', site);
        this.vectorSource.addFeature(feature);
      }

      feature.setStyle(this.getStyle(state));
    }
  }

  private getStyle(state: MarkerState): Style {
    if (this.styleCache.has(state)) return this.styleCache.get(state)!;
    const style = this.buildCanvasStyle(state);
    this.styleCache.set(state, style);
    return style;
  }

  private buildCanvasStyle(state: MarkerState): Style {
    const canvas  = document.createElement('canvas');
    canvas.width  = CANVAS_SIZE;
    canvas.height = CANVAS_SIZE;
    const ctx     = canvas.getContext('2d')!;
    const cx      = CANVAS_SIZE / 2;
    const cy      = CANVAS_SIZE / 2;

    ctx.beginPath();
    ctx.arc(cx, cy, RADIUS, 0, Math.PI * 2);

    if (state === 'site-selected') {
      ctx.lineWidth = 3;
      ctx.fillStyle = COLOR_TEAL; ctx.strokeStyle = COLOR_TEAL;
    } else if (state === 'region-selected') {
      ctx.lineWidth = 3;
      ctx.fillStyle = COLOR_WHITE; ctx.strokeStyle = COLOR_TEAL;
    } else {
      ctx.lineWidth = 1;
      ctx.fillStyle = COLOR_WHITE; ctx.strokeStyle = COLOR_DEFAULT;
    }
    ctx.fill();
    ctx.stroke();

    if (this.globeReady && this.globeImage) this.drawGlobe(ctx, cx, cy, state);

    return new Style({
      image: new Icon({ img: canvas, size: [CANVAS_SIZE, CANVAS_SIZE] as [number, number] }),
      zIndex: state === 'site-selected' ? 2 : state === 'region-selected' ? 1 : 0,
    });
  }

  private drawGlobe(ctx: CanvasRenderingContext2D, cx: number, cy: number, state: MarkerState): void {
    const x = cx - ICON_SIZE / 2;
    const y = cy - ICON_SIZE / 2;
    if (state === 'site-selected' && this.globeImageWhite) {
      ctx.drawImage(this.globeImageWhite, x, y, ICON_SIZE, ICON_SIZE);
    } else if (this.globeImage) {
      ctx.drawImage(this.globeImage, x, y, ICON_SIZE, ICON_SIZE);
    }
  }

  // ── Toggle Maps / Plan ────────────────────────────────────────────────────
  setViewMode(mode: MapViewMode): void {
    this.viewMode.set(mode);
    this.mapService.switchBaseLayer(this.map, mode);
  }

  // ── Click marker ──────────────────────────────────────────────────────────
  // Au clic sur un marker : ouvre la popup (au lieu de naviguer directement).
  // La navigation vers la page site se fait via le bouton "Open" de la popup.
  private setupClickInteraction(): void {
    this.selectInteraction = new Select({ condition: click, style: null });
    this.selectInteraction.on('select', (e) => {
      const feature = e.selected[0];
      if (!feature) {
        // Clic en dehors d'un marker → ferme la popup
        this.closePopup();
        return;
      }
      const site = feature.get('siteData') as SiteFeature;
      this.openPopup(site);
      this.selectInteraction.getFeatures().clear();
    });
    this.map.addInteraction(this.selectInteraction);

    // Ferme la popup si l'utilisateur clique sur la carte (hors marker)
    this.map.on('click', (evt) => {
      const hit = this.map.hasFeatureAtPixel(evt.pixel);
      if (!hit) this.closePopup();
    });
  }
}